import { ExtendedColumnType, useColumnExpansion } from "src/pages/binOverview/BinCommander";
import dayjs from "dayjs";
import { Table } from "antd";
import React, { useMemo } from "react";
import { useGetPremierPlusBinSettingsHistoryQuery } from "src/queries/useGetPremierPlusBinSettingsHistoryQuery";
import PremierPlusSettingHistoryRecordDTO from "src/models/PremierPlusSettingHistoryRecordDTO";
import { useGetPremierPlusBinDto } from "src/queries/useGetPremierPlusBinDto";
import { convertFahrenheitToCelsius, convertFahrenheitToCelsiusOffset } from "src/utils/generalUtils";

interface SettingsTableProps {
    binId: number | null | undefined;
}

interface SettingsTableRow{
    captureDateTime: string;
    maxPlenumTemp: number | null;
    minPlenumTemp: number | null;
    targetPlenumTemp: number | null;
    plenumCal: number | null;
    grainCal: number | null;
    transferMc: number | null; 
    moistureCal: number | null;

}

export const SettingsTable: React.FC<SettingsTableProps> = ({ binId }) => {
    const { getPremierPlusBinSettingsHistoryQuery } = useGetPremierPlusBinSettingsHistoryQuery(binId, null, null, { enabled: true });
    const { getPremierPlusBinDtoQuery } = useGetPremierPlusBinDto(binId ?? -1, { enabled: true, refetchOnWindowFocus: false });
    const useCelsius = useMemo (() => {
        return getPremierPlusBinDtoQuery.data?.platformSettings?.useMetricSystem === true;
    }, [getPremierPlusBinDtoQuery.dataUpdatedAt])

    const data = useMemo(() => {
        const raw = getPremierPlusBinSettingsHistoryQuery.data ?? [];

        const processed : SettingsTableRow[] = raw.map( (item) => ({
            captureDateTime: item.captureTimeUtc,
            maxPlenumTemp: useCelsius ? convertFahrenheitToCelsius(item.maxPlenumTemperatureF) : item.maxPlenumTemperatureF,
            minPlenumTemp: useCelsius ? convertFahrenheitToCelsius(item.minPlenumTemperatureF) : item.minPlenumTemperatureF,
            targetPlenumTemp: useCelsius ? convertFahrenheitToCelsius(item.targetPlenumTemperatureF) : item.targetPlenumTemperatureF,
            plenumCal: useCelsius ? convertFahrenheitToCelsiusOffset(item.plenumTempCalibrationF) : item.plenumTempCalibrationF,
            grainCal: useCelsius ? convertFahrenheitToCelsiusOffset(item.grainTempCalibrationF) : item.grainTempCalibrationF,
            transferMc: item.targetMc,
            moistureCal: item.grainMcCalibration
        }))
        .filter(
            (row, index, self) =>
            index ===
            self.findIndex(
                (other) =>
                other.maxPlenumTemp === row.maxPlenumTemp &&
                other.minPlenumTemp === row.minPlenumTemp &&
                other.targetPlenumTemp === row.targetPlenumTemp &&
                other.plenumCal === row.plenumCal &&
                other.grainCal === row.grainCal &&
                other.transferMc === row.transferMc &&
                other.moistureCal === row.moistureCal
            )
        );

        // ✅ Sort newest first
        return [...processed].sort(
            (a, b) => new Date(b.captureDateTime).getTime() - new Date(a.captureDateTime).getTime()
        );
    }, [getPremierPlusBinSettingsHistoryQuery.data, useCelsius]);



    const columns: Array<ExtendedColumnType<SettingsTableRow>> = useMemo(
        () => [
            {
            title: "Date",
            dataIndex: "captureDateTime",
            width: "180px",
            render(value) {
                return dayjs(value).format("hh:mm A MM/DD/YYYY");
            },
            },
            { title: `Max_Temp ${useCelsius ? "°C" : "°F"}`, dataIndex: "maxPlenumTemp" },
            { title: `Min_Temp ${useCelsius ? "°C" : "°F"}`, dataIndex: "minPlenumTemp" },
            { title: `Target ${useCelsius ? "°C" : "°F"}`, dataIndex: "targetPlenumTemp" },
            { title: `Plenum_Cal ${useCelsius ? "°C" : "°F"}`, dataIndex: "plenumCal" },
            { title: `Grain_Cal ${useCelsius ? "°C" : "°F"}`, dataIndex: "grainCal" },
            { title: "Transfer_Mst", dataIndex: "targetMc" },
            { title: "Moisture_Cal", dataIndex: "moistureCal" },
        ],
    [useCelsius]
    );

    // ✅ Fixed to compare with PREVIOUS row (row above)
    const highlightSettingChanges = (exclusions: Array<string>) => {
        for (const col of columns) {
            if (typeof col.dataIndex !== "string" || !col.dataIndex || exclusions.includes(col.dataIndex)) {
                continue;
            }

            col.onCell = (record, rowIndex) => {
                if (!data || rowIndex === undefined || rowIndex === 0) {
                    return {};
                }

                const dataIndex = col.dataIndex as keyof SettingsTableRow;
                const prevRow = data[rowIndex + 1];
                if (!prevRow) return {};

                const currentVal = record[dataIndex];
                const prevVal = prevRow[dataIndex];

                const currentStr = currentVal?.toString().trim?.();
                const prevStr = prevVal?.toString().trim?.();

                if (currentStr !== prevStr) {
                    return {
                        style: {
                            backgroundColor: "red",
                            color: "white",
                        },
                    };
                }

                return {};
            };
        }
    };

    highlightSettingChanges(["captureDateTime"]);

    const transformedColumns = useColumnExpansion({
        columns: columns,
        datasource: data,
    });

    return (
        <Table
            sticky
            size="small"
            pagination={{ position: ["none", "none"], defaultPageSize: 100 }}
            dataSource={data}
            columns={transformedColumns}
            rowKey={(record) => record.captureDateTime} // ✅ added key for performance
        />
    );
};
